import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { employeeService, type Employee } from '../../api/Employee';
import { incidentService, type Incident } from '../../api/incidents';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import FilterOptions from './components/FilterOptions';
import { getApiErrorMessage } from '../../api';
import {
  AlertTriangle,
  Search,
  RefreshCw,
  Filter,
  MapPin,
  Calendar,
  Clock,
  Car,
  User,
  FileText,
  Eye,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  ImageIcon,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const HistoricoDeIncidentes = () => {
  const { setPageTitle } = useAuth();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Histórico de incidentes do setor');
  }, [setPageTitle]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allIncidents, scopedVehicles, scopedEmployees] = await Promise.all([
        incidentService.getAll(),
        vehicleService.getAllVehicles(),
        employeeService.getAllEmployees(),
      ]);
      setIncidents(allIncidents || []);
      const deduped = Array.from(
        new Map(
          [
            ...(allIncidents || [])
              .filter((incident) => incident.vehicle)
              .map((incident) => incident.vehicle as Vehicle),
            ...(scopedVehicles || []),
          ].map((vehicle) => [vehicle.id, vehicle]),
        ).values(),
      );
      setVehicles(deduped as Vehicle[]);
      setEmployees(scopedEmployees || []);
    } catch (_err) {
      setIncidents([]);
      setVehicles([]);
      setEmployees([]);
      setError(getApiErrorMessage(_err, 'Erro ao buscar histórico de incidentes.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
    (window as unknown as { refreshIncidentes?: () => void }).refreshIncidentes = () => {
      void fetchData();
    };
  }, [fetchData]);

  const filterCategories = useMemo(
    () => ({
      Veículo: Array.from(new Set(incidents.map((incident) => incident.vehicle_id)))
        .map((vehicleId) => {
          const vehicle = vehicles.find((item) => item.id === vehicleId);
          return {
            id: String(vehicleId),
            label: vehicle
              ? `${vehicle.brand || 'Veículo'} - ${vehicle.license_plate}`
              : `Veículo ${vehicleId}`,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
      Fotos: [
        { id: 'com_fotos', label: 'Com fotos' },
        { id: 'sem_fotos', label: 'Sem fotos' },
      ],
    }),
    [incidents, vehicles],
  );

  const filteredIncidents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedVehicles = appliedFilters.Veículo || [];
    const selectedPhotoFilters = appliedFilters.Fotos || [];

    return incidents.filter((i) => {
      const vehicle = vehicles.find((item) => item.id === i.vehicle_id);
      const involvedEmployeesText = (i.involved_employees || [])
        .map((employeeId) => {
          const employee = employees.find((item) => item.id === employeeId);
          return `${employee?.name || ''} ${employee?.cpf || ''}`.trim();
        })
        .join(' ')
        .toLowerCase();

      const matchesSearch =
        !term ||
        String(i.id).includes(term) ||
        (i.description || '').toLowerCase().includes(term) ||
        (i.date || '').toLowerCase().includes(term) ||
        (i.time || '').toLowerCase().includes(term) ||
        (i.location || '').toLowerCase().includes(term) ||
        `${vehicle?.brand || ''} ${vehicle?.license_plate || ''} ${vehicle?.description || ''}`
          .toLowerCase()
          .includes(term) ||
        involvedEmployeesText.includes(term);

      const matchesVehicle =
        selectedVehicles.length === 0 || selectedVehicles.includes(String(i.vehicle_id));

      const hasPhotos = Boolean(i.images?.length);
      const matchesPhotos =
        selectedPhotoFilters.length === 0 ||
        (selectedPhotoFilters.includes('com_fotos') && hasPhotos) ||
        (selectedPhotoFilters.includes('sem_fotos') && !hasPhotos);

      return matchesSearch && matchesVehicle && matchesPhotos;
    });
  }, [incidents, searchTerm, appliedFilters, vehicles, employees]);

  const totalPages = Math.max(1, Math.ceil(filteredIncidents.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredIncidents.length, currentPage, totalPages]);

  const { incidentsPaginados, pageRange } = useMemo(() => {
    const total = filteredIncidents.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      incidentsPaginados: filteredIncidents.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredIncidents, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const getVehicleInfo = useCallback(
    (vehicle_id: number) => {
      const v = vehicles.find((vv) => vv.id === vehicle_id);
      return v
        ? `${v.brand || v.description || 'Veículo'}${v.license_plate ? ` — Placa: ${v.license_plate}` : ''}`
        : String(vehicle_id);
    },
    [vehicles],
  );

  const renderInvolvedEmployeesSummary = useCallback(
    (ids: number[] = []) => {
      const names = ids
        .map((id) => employees.find((e) => e.id === id)?.name)
        .filter((name): name is string => Boolean(name));
      if (names.length === 0) return '-';
      const preview = names.slice(0, 2).join(', ');
      return names.length > 2 ? `${preview} +${names.length - 2}` : preview;
    },
    [employees],
  );

  const renderInvolvedEmployeesFull = useCallback(
    (ids: number[] = []) => {
      if (ids.length === 0) return <span className="text-slate-400">Nenhum envolvido</span>;
      return (
        <div className="flex flex-wrap gap-2">
          {ids.map((id) => {
            const emp = employees.find((e) => e.id === id);
            return emp ? (
              <span
                key={id}
                className="inline-flex flex-col gap-0.5 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2"
              >
                <span className="text-[13px] font-semibold text-emerald-900 leading-tight">
                  {emp.name}
                </span>
                <span className="text-[11.5px] font-medium text-emerald-700 tabular-nums leading-tight">
                  {emp.cpf}
                </span>
              </span>
            ) : null;
          })}
        </div>
      );
    },
    [employees],
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const involvedFullText = useCallback(
    (ids: number[] = []) =>
      ids
        .map((id) => {
          const emp = employees.find((e) => e.id === id);
          return emp ? `${emp.name} — CPF: ${emp.cpf}` : null;
        })
        .filter(Boolean)
        .join('\n'),
    [employees],
  );

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedIncident(null);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Histórico de incidentes do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Histórico — Incidentes
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Registro completo de todos
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                os incidentes do seu departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              variant="secondary"
              onClick={() => setIsFilterOpen(true)}
              className="h-10 px-4"
            >
              <Filter className="h-4 w-4 stroke-[1.9]" />
              Filtros
            </Button>
            <Button
              variant="secondary"
              onClick={() => void fetchData()}
              className="h-10 px-4"
            >
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 inline-flex items-start gap-3 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 min-w-[44px] min-h-[44px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_8px_22px_-10px_rgba(244,63,94,0.7)]">
                  <AlertTriangle className="h-[22px] w-[22px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Todos os incidentes registrados
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Filtre por veículo ou presença de fotos
                  </p>
                </div>
              </div>
              <div className="relative w-full sm:w-[360px] shrink-0">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar ID, local, veículo, envolvidos..."
                  className="!pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && incidents.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-rose-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando histórico...
                </span>
              </div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th className="w-[6%] min-w-[70px]">ID</Th>
                    <Th className="w-[15%] min-w-[190px]">Veículo</Th>
                    <Th className="w-[13%] min-w-[170px]">Local</Th>
                    <Th className="w-[9%] min-w-[110px]">Data</Th>
                    <Th className="w-[8%] min-w-[90px]">Hora</Th>
                    <Th className="w-[20%] min-w-[280px]">Descrição</Th>
                    <Th className="w-[12%] min-w-[170px]">Envolvidos</Th>
                    <Th className="w-[8%] min-w-[110px]">Fotos</Th>
                    <Th className="w-[10%] min-w-[150px]">Criado em</Th>
                    <Th className="w-[5%] min-w-[80px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {incidentsPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={10}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <AlertTriangle className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">
                            Nenhum incidente encontrado
                          </div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ||
                            appliedFilters.Veículo?.length ||
                            appliedFilters.Fotos?.length
                              ? 'Nenhum resultado para os filtros aplicados. Tente outros termos.'
                              : 'Os incidentes aparecerão aqui automaticamente quando forem registrados.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    incidentsPaginados.map((i) => (
                      <Tr key={i.id}>
                        <Td>
                          <span className="inline-flex items-center justify-center h-8 min-w-[40px] px-2 rounded-lg bg-slate-100 text-[12.5px] font-bold tabular-nums text-slate-700 ring-1 ring-inset ring-slate-200">
                            #{i.id}
                          </span>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[260px] overflow-hidden"
                            title={getVehicleInfo(i.vehicle_id)}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[190px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {vehicles.find((v) => v.id === i.vehicle_id)?.brand ||
                                    vehicles.find((v) => v.id === i.vehicle_id)?.description ||
                                    i.vehicle_id}
                                </span>
                                <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                    {vehicles.find((v) => v.id === i.vehicle_id)?.license_plate ||
                                      'Sem placa'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[220px] overflow-hidden"
                            title={i.location || '-'}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[160px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {i.location || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                            <Calendar className="h-[15px] w-[15px] text-slate-400" />
                            {formatDate(i.date)}
                          </div>
                        </Td>
                        <Td>
                          <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                            <Clock className="h-[15px] w-[15px] text-slate-400" />
                            {i.time || '-'}
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-[380px] overflow-hidden">
                            <p
                              className="text-[13px] font-medium leading-snug text-slate-800 line-clamp-2"
                              title={i.description || '-'}
                            >
                              {i.description || '-'}
                            </p>
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[220px] overflow-hidden"
                            title={involvedFullText(i.involved_employees)}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                <User className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[170px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {renderInvolvedEmployeesSummary(i.involved_employees)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center justify-center">
                            <div className="flex flex-wrap items-center justify-center gap-1.5 max-w-[120px]">
                              {i.images && i.images.length > 0 ? (
                                i.images.slice(0, 4).map((img, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => setSelectedImage(img.url)}
                                    className="group relative block h-9 w-9 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
                                    title={`Foto ${idx + 1}${i.images && i.images.length > 4 ? ` (de ${i.images.length})` : ''} — clique para ampliar`}
                                  >
                                    <img
                                      src={img.url}
                                      alt={`Incidente ${idx + 1}`}
                                      className="h-full w-full object-cover"
                                    />
                                  </button>
                                ))
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-[11.5px] font-medium text-slate-400 ring-1 ring-inset ring-slate-200 whitespace-nowrap">
                                  <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
                                  Sem fotos
                                </span>
                              )}
                              {i.images && i.images.length > 4 && (
                                <span className="inline-flex h-9 min-w-[36px] items-center justify-center rounded-lg bg-slate-200/60 px-2 text-[11px] font-bold tabular-nums text-slate-700 ring-1 ring-inset ring-slate-300">
                                  +{i.images.length - 4}
                                </span>
                              )}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[13px] font-medium text-slate-700 tabular-nums">
                              {formatDate(i.created_at)}
                            </span>
                            <span className="text-[11.5px] font-medium text-slate-500 tabular-nums">
                              {i.created_at ? formatDateTime(i.created_at).split(' ').slice(-2).join(' ') : '-'}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center justify-center gap-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedIncident(i);
                                setIsViewModalOpen(true);
                              }}
                              title="Visualizar detalhes"
                              className="h-8 w-8 rounded-lg text-slate-500 hover:text-rose-700 hover:bg-rose-50 hover:ring-rose-200"
                            >
                              <Eye className="h-4 w-4 stroke-[1.9]" />
                            </Button>
                          </div>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.start}
                  </span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.end}
                  </span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.total}
                  </span>
                  {' incidente(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_4px_14px_-4px_rgba(244,63,94,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedIncident && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(244,63,94,0.65)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Incidente #{selectedIncident.id}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Visualize todos os dados, envolvidos e fotos
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Dados do Incidente
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">ID</span>
                    <span className="inline-flex items-center h-9 max-w-[90px] px-3 rounded-xl bg-slate-100 text-[13px] font-bold tabular-nums text-slate-700 ring-1 ring-inset ring-slate-200 w-fit">
                      #{selectedIncident.id}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {getVehicleInfo(selectedIncident.vehicle_id)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Local</span>
                    <div className="min-w-0 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-indigo-500 shrink-0 stroke-[1.9]" />
                      <p
                        className="min-w-0 text-sm text-slate-900 font-medium truncate"
                        title={selectedIncident.location || '-'}
                      >
                        {selectedIncident.location || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Data</span>
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800 tabular-nums">
                      <Calendar className="h-4 w-4 text-slate-500 stroke-[1.9]" />
                      {formatDate(selectedIncident.date)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Hora</span>
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800 tabular-nums">
                      <Clock className="h-4 w-4 text-slate-500 stroke-[1.9]" />
                      {selectedIncident.time || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Criado em</span>
                    <span className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDateTime(selectedIncident.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                      Descrição
                    </span>
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[80px] leading-relaxed">
                    {selectedIncident.description || '-'}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Funcionários Envolvidos
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {renderInvolvedEmployeesFull(selectedIncident.involved_employees)}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">
                  Galeria de Fotos
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {selectedIncident.images && selectedIncident.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {selectedIncident.images.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedImage(img.url)}
                        className="group relative block w-full aspect-square overflow-hidden rounded-2xl border border-slate-200 shadow-sm transition-all hover:shadow-lg hover:scale-[1.02]"
                        title={`Foto ${idx + 1} — clique para ampliar`}
                      >
                        <img
                          src={img.url}
                          alt={`Incidente ${idx + 1}`}
                          className="h-full w-full object-cover transition-all group-hover:scale-105"
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent p-2.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <span className="text-[11px] font-bold text-white drop-shadow">
                            Ver foto #{idx + 1}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-slate-400">
                    <ImageIcon className="h-10 w-10 stroke-[1.7]" />
                    <span className="text-[13px] font-semibold">
                      Nenhuma foto registrada para este incidente
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => setSelectedImage(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm transition-all hover:bg-white/20 hover:scale-105"
            onClick={() => setSelectedImage(null)}
            aria-label="Fechar"
          >
            <X className="h-6 w-6 stroke-[2]" />
          </button>
          <img
            src={selectedImage}
            alt="Foto ampliada do incidente"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl border-4 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(filters) => {
          setAppliedFilters(filters);
          setCurrentPage(1);
        }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </div>
  );
};

export default HistoricoDeIncidentes;
